import React, { useContext } from "react";
import { UseFormReturn } from "react-hook-form";
import { useLocation } from "react-router";
import { ApiError } from "go-core/api/types";
import {
	OrderPaymentMethodApi,
	OrderPaymentMethodType,
	OrderTransactionApi,
	OrderTransactionStatus,
	PayOrderData,
} from "../../../../../../../services/Api/Public/types";
import OrderProcessPayment from "../../../components/OrderProcessPayment";
import PaymentInfoModal from "../../../components/PaymentInfoModal";
import StickyFooter from "../../../components/StickyFooter";
import { OrderContext } from "../../../contexts/OrderContext";
import { validateOrderPaymentData } from "../../../utils/validation";
import { getEOrderTotalPriceWithTip } from "../utils";
import CustomPayButton from "./Payments/Buttons/CustomPayButton";
import PayViaApplePayButton from "./Payments/Buttons/PayViaApplePayButton";
import PayViaBlik from "./Payments/Buttons/PayViaBlik";
import PayViaGooglePayButton from "./Payments/Buttons/PayViaGooglePayButton";

interface Props {
	selectedPaymentMethod: OrderPaymentMethodApi | undefined;
	form: UseFormReturn<PayOrderData>;
	handlePayment: (
		paymentData: string,
		callback?: (data: OrderTransactionApi) => Promise<void> | void
	) => (e?: React.BaseSyntheticEvent) => Promise<void>;
	isProcessing: boolean;
	setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
	showErrorPaymentModal: boolean;
	setShowErrorPaymentModal: React.Dispatch<React.SetStateAction<boolean>>;
	customFormErrors: ApiError[];
	setCustomFormErrors: React.Dispatch<React.SetStateAction<ApiError[]>>;
	sendInvoice: boolean;
	checkTransaction: (data: OrderTransactionApi) => Promise<void>;
}

const OrderPaymentStickyFooter = ({
	selectedPaymentMethod,
	form,
	handlePayment,
	isProcessing,
	setIsProcessing,
	showErrorPaymentModal,
	setShowErrorPaymentModal,
	customFormErrors,
	setCustomFormErrors,
	sendInvoice,
	checkTransaction,
}: Props) => {
	const { order, orderId } = useContext(OrderContext);
	const { watch } = form;
	const shouldRender = order && orderId && selectedPaymentMethod;
	const { search } = useLocation();

	if (!shouldRender) return <></>;

	const validatePaymentDataOnClick = () => {
		return validateOrderPaymentData(watch(), sendInvoice, setCustomFormErrors);
	};

	const totalPrice = getEOrderTotalPriceWithTip(order, search);

	const renderPayment = () => {
		switch (selectedPaymentMethod?.type) {
			case OrderPaymentMethodType.GOOGLE_PAY: {
				if (order?.google_merchant_id) {
					return (
						<PayViaGooglePayButton
							handleOnLoadPayment={handlePayment}
							totalPrice={totalPrice}
							setIsProcessing={setIsProcessing}
							customFormErrors={customFormErrors}
							validatePaymentDataOnClick={validatePaymentDataOnClick}
							merchantId={order.google_merchant_id}
							checkTransaction={checkTransaction}
						/>
					);
				}
				return (
					<CustomPayButton
						totalPrice={totalPrice}
						handlePay={handlePayment}
						validatePaymentDataOnClick={validatePaymentDataOnClick}
						checkTransaction={checkTransaction}
					/>
				);
			}
			case OrderPaymentMethodType.BLIK:
				return (
					<PayViaBlik
						totalPrice={totalPrice}
						handlePay={handlePayment}
						setShowErrorPaymentModal={setShowErrorPaymentModal}
						setIsProcessing={setIsProcessing}
						form={form}
						validatePaymentDataOnClick={validatePaymentDataOnClick}
						checkTransaction={checkTransaction}
					/>
				);
			case OrderPaymentMethodType.APPLE_PAY:
				if (order?.apple_pay_on_site) {
					return (
						<PayViaApplePayButton
							totalPrice={totalPrice}
							validatePaymentDataOnClick={validatePaymentDataOnClick}
							paymentMethod={selectedPaymentMethod}
							handlePay={handlePayment}
							checkTransaction={checkTransaction}
						/>
					);
				}
				return (
					<CustomPayButton
						totalPrice={totalPrice}
						handlePay={handlePayment}
						validatePaymentDataOnClick={validatePaymentDataOnClick}
						checkTransaction={checkTransaction}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<StickyFooter className="order-payment-sticky-footer" dataTestId="e-order-payment-sticky-footer">
			{renderPayment()}
			{showErrorPaymentModal && (
				<PaymentInfoModal
					status={OrderTransactionStatus.ERROR}
					show={showErrorPaymentModal}
					onHide={() => setShowErrorPaymentModal(false)}
				/>
			)}
			{isProcessing && <OrderProcessPayment paymentType={selectedPaymentMethod?.type} />}
		</StickyFooter>
	);
};

export default OrderPaymentStickyFooter;
